/**
 * NavigationProvider - Context Provider for Navigation System
 *
 * Manages the shell's responsive, collapsed, and active-route state.
 *
 * Performance optimizations:
 * - Split into separate state and actions contexts to prevent unnecessary re-renders
 * - All actions memoized with useCallback
 * - State object memoized with useMemo
 *
 * @module NavigationProvider
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { getActiveNavigationState, MOBILE_BREAKPOINT } from '../constants';

// Separate contexts for state and actions (performance optimization)
const NavigationContext = createContext(null);
const NavigationActionsContext = createContext(null);

/**
 * NavigationProvider Component
 *
 * Wrap your app with this provider to enable navigation context
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const NavigationProvider = ({ children }) => {
  // State
  const [isMobileView, setIsMobileView] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Get current location for active menu detection
  const location = useLocation();

  // Determine active menu from URL (memoized)
  const activeState = useMemo(
    () => getActiveNavigationState(location.pathname),
    [location.pathname]
  );
  const activeMenu = activeState.activeId;
  const activeAncestor = activeState.ancestorId;

  // Mobile detection with matchMedia API (more efficient than resize listener)
  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const handleChange = (e) => {
      setIsMobileView(e.matches);
      // Close sidebar when switching to desktop
      if (!e.matches) {
        setIsSidebarOpen(false);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Actions (all memoized with useCallback for performance)
  const actions = useMemo(() => {
    /**
     * Toggle sidebar open/closed
     */
    const toggleSidebar = () => {
      setIsSidebarOpen(prev => !prev);
    };

    /**
     * Open sidebar
     */
    const openSidebar = () => {
      setIsSidebarOpen(true);
    };

    /**
     * Close sidebar
     */
    const closeSidebar = () => {
      setIsSidebarOpen(false);
    };

    /**
     * Toggle sidebar collapse state
     */
    const toggleCollapse = () => {
      setIsCollapsed(prev => !prev);
    };

    return {
      toggleSidebar,
      openSidebar,
      closeSidebar,
      toggleCollapse
    };
  }, []);

  // Memoize state object to prevent unnecessary re-renders
  const state = useMemo(() => ({
    isMobileView,
    isSidebarOpen,
    activeMenu,
    activeAncestor,
    isCollapsed
  }), [isMobileView, isSidebarOpen, activeMenu, activeAncestor, isCollapsed]);

  return (
    <NavigationContext.Provider value={state}>
      <NavigationActionsContext.Provider value={actions}>
        {children}
      </NavigationActionsContext.Provider>
    </NavigationContext.Provider>
  );
};

NavigationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access navigation state
 * @returns {Object} Navigation state
 * @throws {Error} If used outside NavigationProvider
 */
export const useNavigationState = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationState must be used within NavigationProvider');
  }
  return context;
};

/**
 * Hook to access navigation actions
 * @returns {Object} Navigation actions
 * @throws {Error} If used outside NavigationProvider
 */
export const useNavigationActions = () => {
  const context = useContext(NavigationActionsContext);
  if (!context) {
    throw new Error('useNavigationActions must be used within NavigationProvider');
  }
  return context;
};

/**
 * Combined hook for both state and actions
 * @returns {Object} Combined state and actions
 */
export const useNavigation = () => {
  const state = useNavigationState();
  const actions = useNavigationActions();
  return { ...state, ...actions };
};
